// ConsultedPanel — in-line expansion under the reply listing cited sources.
// Each row: number · type badge · title · domain eyebrow. Click a row to
// reveal the excerpt (per designer Q3). No relevance scores to end users
// (they stay in Labs).
//
// V1 scope: cited sources only. The "N more consulted, not cited" collapsed
// group (also from Q3) depends on the engine surfacing retrieved-but-not-
// cited chunks on the wire — small follow-up MR once we add that debug
// field to the consumer response.

import { useState } from 'react';
import type { CoachCitation } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { SourceConfidence } from './SourceConfidence.js';
import { SourceTypeBadge } from './SourceTypeBadge.js';
import styles from './Coach.module.css';

export interface ConsultedPanelProps {
  citations: readonly CoachCitation[];
  /** 1-indexed citation number the user clicked to open the panel. Panel
   *  pre-expands that row's excerpt. `null` means user opened via the
   *  SourcesLine (no specific row). */
  highlightNumber?: number | null;
}

export function ConsultedPanel({ citations, highlightNumber }: ConsultedPanelProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(
    highlightNumber ? highlightNumber - 1 : null,
  );

  return (
    <div className={styles.consultedPanel}>
      {citations.map((c, i) => (
        <ConsultedRow
          key={i}
          citation={c}
          number={i + 1}
          highlighted={highlightNumber === i + 1}
          open={openIndex === i}
          onToggle={() => setOpenIndex(openIndex === i ? null : i)}
        />
      ))}
    </div>
  );
}

interface ConsultedRowProps {
  citation: CoachCitation;
  number: number;
  highlighted: boolean;
  open: boolean;
  onToggle: () => void;
}

function ConsultedRow({ citation, number, highlighted, open, onToggle }: ConsultedRowProps) {
  const { t } = useI18n();
  const canExpand = Boolean(citation.excerpt);
  return (
    <div
      className={`${styles.consultedRow} ${highlighted ? styles.consultedRowHighlighted : ''}`}
      data-citation-number={number}
    >
      <button
        type="button"
        className={styles.consultedRowHead}
        onClick={canExpand ? onToggle : undefined}
        aria-expanded={canExpand ? open : undefined}
        disabled={!canExpand}
      >
        <span className={styles.consultedNum}>{number}</span>
        {citation.type && <SourceTypeBadge type={citation.type} />}
        <span className={styles.consultedTitle}>
          {citation.href ? (
            <a
              href={citation.href}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {citation.title}
            </a>
          ) : (
            citation.title
          )}
        </span>
        {citation.domain && <span className={styles.consultedDomain}>{citation.domain}</span>}
        {typeof citation.score === 'number' && <SourceConfidence score={citation.score} />}
        {canExpand && (
          <span className={styles.consultedCaret} aria-hidden="true">
            {open ? '▴' : '▾'}
          </span>
        )}
      </button>
      {open && citation.excerpt && (
        <div className={styles.consultedExcerpt}>
          {citation.excerpt}
          {/* "Used in paragraph N" anchor is deferred until the engine tracks
              per-citation paragraph positions. The strings key sources.usedIn
              will be used once that's in. */}
          <span className={styles.consultedUsedPlaceholder}>{t('sources.usedPlaceholder')}</span>
        </div>
      )}
    </div>
  );
}
